/**
 * TimerSystem - manages time-based callbacks integrated with the world's play state.
 */

import * as THREE from 'three';

import { Playable, PlayState } from '../game/Playable.js';

import type { World } from '../game/World.js';

/** Timer event callback function */
export type TimerCallback = (deltaTime: number) => void;

/** Represents a scheduled timer event */
export interface TimerEvent {
    /** Unique identifier for the timer event */
    id: number;

    /** The callback function to execute when the timer fires */
    callback: TimerCallback;

    /** The total delay in seconds before the timer fires */
    delay: number;

    /** The remaining time in seconds before the timer fires */
    remaining: number;

    /** Whether this timer should repeat after firing */
    repeat: boolean;

    /** The interval in seconds for repeating timers. Only used when repeat is true. */
    interval?: number;

    /** Whether this timer is currently active */
    active: boolean;

    /** Optional tag for grouping and managing related timers */
    tag?: string;
}

/** Configuration options for initializing a TimerSystem instance */
export interface TimerSystemOptions {
    /** The World instance that this timer system belongs to. Used to check play state before processing timer events. */
    world: World;
}

/** Centralized timer management system for handling time-based events. */
export class TimerSystem extends Playable {
  private timers: Map<number, TimerEvent> = new Map();
  private nextTimerId: number = 1;
  private timerEnabled: boolean = true;
  private timeScale: number = 1.0;

  /**
   * Creates a new TimerSystem instance.
   * @param options - Required configuration including World reference
   */
  constructor(private options: TimerSystemOptions) {
    super();
  }

  /**
   * Schedules a callback to be executed after a specified delay.
   * @param delaySeconds - The time in seconds to wait before executing the callback
   * @param tag - Optional tag for grouping related timers
   * @returns A timer ID that can be used to cancel the timer
   */
  public setTimeout(callback: TimerCallback, delaySeconds: number, tag?: string): number {
    const id = this.nextTimerId++;
    this.timers.set(id, {
      id,
      callback,
      delay: delaySeconds,
      remaining: delaySeconds,
      repeat: false,
      active: true,
      tag
    });
    return id;
  }

  /**
   * Schedules a callback to be executed repeatedly at the specified interval.
   * @param intervalSeconds - The time in seconds between each execution of the callback
   * @param tag - Optional tag for grouping related timers
   * @returns A timer ID that can be used to cancel the timer
   */
  public setInterval(callback: TimerCallback, intervalSeconds: number, tag?: string): number {
    const id = this.nextTimerId++;
    this.timers.set(id, {
      id,
      callback,
      delay: intervalSeconds,
      remaining: intervalSeconds,
      repeat: true,
      interval: intervalSeconds,
      active: true,
      tag
    });
    return id;
  }

  /**
   * Cancels a scheduled timer event by ID.
   * @returns True if a timer was found and cancelled, false otherwise
   */
  public clearTimer(id: number): boolean {
    if (this.timers.has(id)) {
      this.timers.delete(id);
      return true;
    }
    return false;
  }

  /**
   * Cancels all scheduled timer events with the specified tag.
   * @returns The number of timers that were cancelled
   */
  public clearTimersByTag(tag: string): number {
    let count = 0;
    this.timers.forEach((timer, id) => {
      if (timer.tag === tag) {
        this.timers.delete(id);
        count++;
      }
    });
    return count;
  }

  /**
   * Enables or disables timer processing.
   * @param enabled - When true, timers will be processed; when false, all timers are paused
   */
  public setTimerEnabled(enabled: boolean): void {
    this.timerEnabled = enabled;
  }

  /** @returns True if timer processing is enabled */
  public isTimerEnabled(): boolean {
    return this.timerEnabled;
  }

  /**
   * Sets the time scale factor for all timers.
   * @param scale - The scale factor (1.0 = normal speed, 0.5 = half speed, 2.0 = double speed)
   */
  public setTimeScale(scale: number): void {
    this.timeScale = Math.max(0.0001, scale); // Prevent zero or negative time scale
  }

  /** @returns The current time scale factor */
  public getTimeScale(): number {
    return this.timeScale;
  }

  /**
   * Updates all active timers based on elapsed time.
   * @param deltaTime - Time elapsed since the last frame in seconds
   */
  @THREE.profile
  public tick(deltaTime: number): void {
    if (this.playState !== PlayState.Playing || !this.timerEnabled || !this.options.world.isPlaying()) {
      return;
    }

    // Apply time scaling to delta time
    const scaledDeltaTime = deltaTime * this.timeScale;

    // Store timers that need to be removed
    const timersToRemove: number[] = [];
    const timersToFire: TimerEvent[] = [];

    // First pass: update remaining time and collect timers that need to fire
    this.timers.forEach((timer, id) => {
      if (!timer.active) return;

      timer.remaining -= scaledDeltaTime;

      if (timer.remaining <= 0) {
        // Add to the list of timers to fire
        timersToFire.push(timer);

        if (timer.repeat && timer.interval) {
          // Reset the timer for the next interval
          // Add any overflow time to ensure accurate timing for fast intervals
          const overflowTime = -timer.remaining;
          timer.remaining = timer.interval - (overflowTime % timer.interval);
        } else {
          // Mark one-time timers for removal
          timersToRemove.push(id);
        }
      }
    });

    // Second pass: fire callbacks (done separately to avoid modifying the map during iteration)
    timersToFire.forEach(timer => {
      try {
        timer.callback(scaledDeltaTime);
      } catch (error) {
        console.error(`Error in timer callback (ID: ${timer.id}):`, error);
      }
    });

    // Third pass: remove completed one-time timers
    timersToRemove.forEach(id => {
      this.timers.delete(id);
    });
  }

  /** Pauses all active timers */
  public pauseAllTimers(): void {
    this.timers.forEach(timer => {
      timer.active = false;
    });
  }

  /**
   * Pauses all timers with the specified tag.
   * @returns The number of timers that were paused
   */
  public pauseTimersByTag(tag: string): number {
    let count = 0;
    this.timers.forEach(timer => {
      if (timer.tag === tag && timer.active) {
        timer.active = false;
        count++;
      }
    });
    return count;
  }

  /** Resumes all paused timers */
  public resumeAllTimers(): void {
    this.timers.forEach(timer => {
      timer.active = true;
    });
  }

  /**
   * Resumes all timers with the specified tag.
   * @returns The number of timers that were resumed
   */
  public resumeTimersByTag(tag: string): number {
    let count = 0;
    this.timers.forEach(timer => {
      if (timer.tag === tag && !timer.active) {
        timer.active = true;
        count++;
      }
    });
    return count;
  }

  /** Cancels all active timers */
  public clearAllTimers(): void {
    this.timers.clear();
  }

  /** @returns The number of active timers */
  public getActiveTimerCount(): number {
    let count = 0;
    this.timers.forEach(timer => {
      if (timer.active) count++;
    });
    return count;
  }

  /**
   * Gets the count of timers with a specific tag.
   * @returns The number of timers with the specified tag
   */
  public getTimerCountByTag(tag: string): number {
    let count = 0;
    this.timers.forEach(timer => {
      if (timer.tag === tag) count++;
    });
    return count;
  }

  protected override doBeginPlay(): void {
    // Resume all timers when play begins
    this.resumeAllTimers();
  }

  protected override doEndPlay(): void {
    // Pause all timers when play ends
    this.pauseAllTimers();
  }
}